import { gql, useQuery } from "@apollo/client";
import React, { useEffect, useMemo } from "react";
import { getResizedCatalogImageUrl } from "./textureCache";
import {
  BuiltInFurnitureMaterial,
  SurfaceCatalogApiRow,
  builtInFurnitureMaterialToSurfaceRef,
  getBuiltInFurnitureMaterial,
  listBuiltInFurnitureMaterials,
  setSurfaceCatalogMaterialsFromApi,
} from "./builtInFurnitureMaterials";
import { CatalogSurfaceReference } from "../products/product3DTypes";

export type BuiltInMaterialSurfaceMapping = {
  builtInMaterialId: string;
  surfaceId?: string;
  imageUrl?: string;
  surfaceName?: string;
  /** Raw Surface.images used for Ready/bake detection. */
  surfaceImages?: Array<string | null> | null;
  /** true when this Surface is baked and selectable. */
  enabled: boolean;
  materialKind?: string;
};

export type BuiltInMaterialBakeStatus = "baked" | "not_baked" | "no_image";

/**
 * Bake quality (optional): SurfaceAlbedo_ / albedo URL markers mean AI-baked
 * tileable. Pack membership + images[0] is what makes a material usable —
 * bake is not required for visibility.
 */
export function getBuiltInMaterialBakeStatus(
  images?: Array<string | null> | null,
): BuiltInMaterialBakeStatus {
  const albedo = images?.[0] ?? null;
  if (!albedo) return "no_image";

  const url = albedo.toLowerCase();
  if (
    url.includes("surfacealbedo_") ||
    url.includes("albedo") ||
    url.includes("baked") ||
    url.includes("tileable")
  ) {
    return "baked";
  }

  return "not_baked";
}

/** True when the Surface has a primary image usable as catalog texture. */
export function surfaceHasCatalogPhoto(
  images?: Array<string | null> | null,
): boolean {
  return Boolean((images?.[0] ?? "").trim());
}

/**
 * Ready = in the pack with a primary photo (images[0]). Procedural presets are
 * always ready. Soft-launch publish = pack membership (Remove from pack to hide).
 */
export function isBuiltInMaterialReady(
  mapping: BuiltInMaterialSurfaceMapping | undefined,
): boolean {
  if (!mapping || mapping.enabled !== true) return false;
  return surfaceHasCatalogPhoto(mapping.surfaceImages);
}

type FloorplannerMaterialSurfacesData = {
  floorplannerMaterialSurfaces?: Array<{
    id: string;
    name?: string | null;
    images?: Array<string | null> | null;
    roughness?: number | null;
    sheen?: number | null;
    sheenRoughness?: number | null;
    repeatScale?: number | null;
    categories?: Array<{
      id?: string | null;
      name?: string | null;
      order?: number | null;
    } | null> | null;
  }> | null;
};

export const FLOORPLANNER_MATERIAL_SURFACES_QUERY = gql`
  query FloorplannerMaterialSurfaces {
    floorplannerMaterialSurfaces {
      id
      name
      images
      roughness
      sheen
      sheenRoughness
      repeatScale
      categories {
        id
        name
        order
      }
    }
  }
`;

/** @deprecated Prefer FLOORPLANNER_MATERIAL_SURFACES_QUERY */
export const FLOORPLANNER_STANDARD_MATERIALS_QUERY =
  FLOORPLANNER_MATERIAL_SURFACES_QUERY;

/** @deprecated Prefer FLOORPLANNER_MATERIAL_SURFACES_QUERY */
export const FLOORPLANNER_BUILT_IN_MATERIAL_SURFACES_QUERY =
  FLOORPLANNER_MATERIAL_SURFACES_QUERY;

let mappingsByMaterialId = new Map<string, BuiltInMaterialSurfaceMapping>();

/**
 * Replace the in-memory Surface overlays used when resolving catalog textures.
 */
export function setBuiltInMaterialSurfaceMappings(
  mappings: BuiltInMaterialSurfaceMapping[],
): void {
  mappingsByMaterialId = new Map(
    mappings.map((mapping) => [mapping.builtInMaterialId, mapping]),
  );
}

export function getBuiltInMaterialSurfaceMapping(
  materialId: string | undefined,
): BuiltInMaterialSurfaceMapping | undefined {
  if (!materialId) return undefined;
  return mappingsByMaterialId.get(materialId);
}

export function isBuiltInMaterialVisibleInFloorplanner(
  materialId: string,
): boolean {
  const material = getBuiltInFurnitureMaterial(materialId);
  if (!material) return false;
  // Procedural metal/glass presets are always available.
  if (material.source === "material") return true;
  return isBuiltInMaterialReady(mappingsByMaterialId.get(materialId));
}

/**
 * Picker pack: procedural presets + pack Surfaces that have a primary photo.
 */
export function listVisibleBuiltInFurnitureMaterials(): BuiltInFurnitureMaterial[] {
  return listBuiltInFurnitureMaterials().filter((material) =>
    isBuiltInMaterialVisibleInFloorplanner(material.id),
  );
}

/** Stable order for Material menu category tiles. */
const BUILT_IN_CATEGORY_ORDER = [
  "wood",
  "metal",
  "fabric",
  "leather",
  "glass",
  "stone",
  "concrete",
  "rattan",
] as const;

export type BuiltInMaterialCategoryGroup = {
  categoryKey: string;
  categoryLabel: string;
  preview: BuiltInFurnitureMaterial;
  materials: BuiltInFurnitureMaterial[];
};

/** Category key for grouping Ready materials in the Material menu. */
export function getBuiltInMaterialCategoryKey(
  material: BuiltInFurnitureMaterial,
): string {
  const fromName = material.categoryName?.trim().toLowerCase();
  if (fromName) return fromName;
  return material.families[0] ?? "other";
}

/**
 * Group visible Ready materials by category for the compact Material menu.
 * Preview is the first material in each group (list order).
 */
export function listVisibleBuiltInFurnitureMaterialsByCategory(): BuiltInMaterialCategoryGroup[] {
  const groups = new Map<string, BuiltInFurnitureMaterial[]>();
  listVisibleBuiltInFurnitureMaterials().forEach((material) => {
    const key = getBuiltInMaterialCategoryKey(material);
    const list = groups.get(key);
    if (list) {
      list.push(material);
    } else {
      groups.set(key, [material]);
    }
  });

  const keys = Array.from(groups.keys()).sort((a, b) => {
    const ai = BUILT_IN_CATEGORY_ORDER.indexOf(
      a as (typeof BUILT_IN_CATEGORY_ORDER)[number],
    );
    const bi = BUILT_IN_CATEGORY_ORDER.indexOf(
      b as (typeof BUILT_IN_CATEGORY_ORDER)[number],
    );
    const aOrder = ai === -1 ? BUILT_IN_CATEGORY_ORDER.length : ai;
    const bOrder = bi === -1 ? BUILT_IN_CATEGORY_ORDER.length : bi;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.localeCompare(b);
  });

  return keys.map((categoryKey) => {
    const materials = groups.get(categoryKey) ?? [];
    const labelSource =
      materials[0]?.categoryName?.trim() ||
      categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1);
    return {
      categoryKey,
      categoryLabel: labelSource,
      preview: materials[0],
      materials,
    };
  });
}

export function clearBuiltInMaterialSurfaceMappingsForTests(): void {
  mappingsByMaterialId = new Map();
}

/**
 * Build a lean CatalogSurfaceReference from a pack material (no imageUrl).
 * Overlay presence forces white tint so albedo is not multiplied once bound.
 */
export function builtInFurnitureMaterialToSurfaceRefWithOverlay(
  material: BuiltInFurnitureMaterial,
): CatalogSurfaceReference {
  const base = builtInFurnitureMaterialToSurfaceRef(material);
  if (material.source !== "surface") {
    return base;
  }
  const overlay = getBuiltInMaterialSurfaceMapping(material.id);
  if (!overlay?.imageUrl) {
    return base;
  }
  return {
    ...base,
    color: 0xffffff,
    // Keep cloth sheen / UV scale from the catalog preset when overlaying albedo.
    roughness: base.roughness ?? material.roughness,
    metalness: base.metalness ?? material.metalness,
    repeatScale: base.repeatScale ?? material.repeatScale,
    sheen: base.sheen ?? material.sheen,
    sheenRoughness: base.sheenRoughness ?? material.sheenRoughness,
    sheenColor: base.sheenColor ?? material.sheenColor,
  };
}

/**
 * When a slot already stores a Surface id, merge the latest baked albedo.
 * Prefer live catalog `imageUrl`; keep a stored URL only when catalog has none
 * (legacy floorplans written before lean surfaceRefs).
 */
export function overlayBuiltInMaterialSurfaceRef(
  surfaceRef: CatalogSurfaceReference | undefined,
  materialId?: string,
): CatalogSurfaceReference | undefined {
  if (!surfaceRef) return undefined;
  if (surfaceRef.source === "material" || surfaceRef.source === "product") {
    return surfaceRef;
  }
  const id = materialId ?? surfaceRef.id;
  const overlay = getBuiltInMaterialSurfaceMapping(id);
  const builtIn = getBuiltInFurnitureMaterial(id);
  // Live pack bake → legacy snapshot → built-in catalog texture (lean refs).
  const imageUrl =
    overlay?.imageUrl ?? surfaceRef.imageUrl ?? builtIn?.textureUrl;
  if (!imageUrl && !builtIn) {
    return surfaceRef;
  }
  return {
    ...surfaceRef,
    ...(imageUrl ? { imageUrl } : {}),
    color: surfaceRef.color ?? (imageUrl ? 0xffffff : builtIn?.color),
    roughness: surfaceRef.roughness ?? builtIn?.roughness,
    metalness: surfaceRef.metalness ?? builtIn?.metalness,
    repeatScale: surfaceRef.repeatScale ?? builtIn?.repeatScale,
    sheen: surfaceRef.sheen ?? builtIn?.sheen,
    sheenRoughness: surfaceRef.sheenRoughness ?? builtIn?.sheenRoughness,
    sheenColor: surfaceRef.sheenColor ?? builtIn?.sheenColor,
  };
}

/**
 * Resolve a persisted (possibly lean) surfaceRef for 3D rendering.
 * Live catalog albedo wins; legacy embedded imageUrl is the fallback.
 */
export function resolveCatalogSurfaceRefForRender(
  surfaceRef: CatalogSurfaceReference | undefined,
  materialId?: string,
): CatalogSurfaceReference | undefined {
  if (!surfaceRef) return undefined;
  const id = materialId ?? surfaceRef.id;
  const builtIn = getBuiltInFurnitureMaterial(id);

  // Sellable wallpaper (and similar) products store their own albedo URL.
  if (surfaceRef.source === "product") {
    return surfaceRef;
  }

  // Procedural / material-source presets have no catalog albedo overlay.
  if (surfaceRef.source === "material") {
    if (!builtIn?.textureUrl && !surfaceRef.imageUrl) {
      return surfaceRef;
    }
    return {
      ...surfaceRef,
      // Prefer legacy snapshot when present; else built-in texture.
      ...(builtIn?.textureUrl || surfaceRef.imageUrl
        ? { imageUrl: surfaceRef.imageUrl ?? builtIn?.textureUrl }
        : {}),
      repeatScale: surfaceRef.repeatScale ?? builtIn?.repeatScale,
      roughness: surfaceRef.roughness ?? builtIn?.roughness,
      metalness: surfaceRef.metalness ?? builtIn?.metalness,
      sheen: surfaceRef.sheen ?? builtIn?.sheen,
      sheenRoughness: surfaceRef.sheenRoughness ?? builtIn?.sheenRoughness,
      sheenColor: surfaceRef.sheenColor ?? builtIn?.sheenColor,
    };
  }

  return overlayBuiltInMaterialSurfaceRef(surfaceRef, id);
}

function normalizeFromCatalogSurfaces(
  rows: FloorplannerMaterialSurfacesData["floorplannerMaterialSurfaces"],
): {
  apiRows: SurfaceCatalogApiRow[];
  mappings: BuiltInMaterialSurfaceMapping[];
} {
  const list = rows ?? [];
  const mappings: BuiltInMaterialSurfaceMapping[] = list.map((row) => {
    const surfaceImages = row.images ?? null;
    const rawImage = (surfaceImages?.[0] ?? "").trim() || undefined;
    const hasPhoto = Boolean(rawImage);
    return {
      builtInMaterialId: row.id,
      surfaceId: row.id,
      surfaceName: row.name ?? undefined,
      surfaceImages,
      imageUrl: rawImage ? getResizedCatalogImageUrl(rawImage) : undefined,
      // Pack membership comes from the query; photo makes it selectable in 3D.
      enabled: hasPhoto,
    };
  });

  // Load pack Surfaces that have a primary photo (bake optional).
  const apiRows: SurfaceCatalogApiRow[] = list
    .filter((row) => surfaceHasCatalogPhoto(row.images))
    .map((row) => ({
      id: row.id,
      name: row.name,
      images: row.images,
      roughness: row.roughness,
      sheen: row.sheen,
      sheenRoughness: row.sheenRoughness,
      repeatScale: row.repeatScale,
      categories: row.categories,
    }));

  return { apiRows, mappings };
}

export function getBuiltInMaterialSurfaceMappingsSignature(
  mappings: BuiltInMaterialSurfaceMapping[],
): string {
  return mappings
    .map(
      (mapping) =>
        `${mapping.builtInMaterialId}:${mapping.enabled ? 1 : 0}:${
          mapping.imageUrl ?? ""
        }`,
    )
    .join("|");
}

/** Revision string so mesh observers re-resolve when the pack loads. */
export const BuiltInMaterialSurfaceMappingsContext =
  React.createContext<string>("");

export function useBuiltInMaterialSurfaceMappingsRevision(): string {
  return React.useContext(BuiltInMaterialSurfaceMappingsContext);
}

/**
 * Fetches Floorplanner materials Surfaces and keeps the module cache in sync
 * for mesh resolvers + the Material picker.
 */
export function useFloorplannerBuiltInMaterialSurfaces(enabled = true) {
  const { data, loading, error, refetch } =
    useQuery<FloorplannerMaterialSurfacesData>(
      FLOORPLANNER_MATERIAL_SURFACES_QUERY,
      {
        skip: !enabled,
        fetchPolicy: "cache-and-network",
      },
    );

  const { mappings, apiRows } = useMemo(
    () => normalizeFromCatalogSurfaces(data?.floorplannerMaterialSurfaces),
    [data],
  );
  const signature = useMemo(
    () => getBuiltInMaterialSurfaceMappingsSignature(mappings),
    [mappings],
  );

  useEffect(() => {
    if (!enabled) return;
    setSurfaceCatalogMaterialsFromApi(apiRows);
    setBuiltInMaterialSurfaceMappings(mappings);
  }, [enabled, apiRows, mappings]);

  return {
    mappings,
    signature,
    loading,
    error,
    refetch,
    getMaterial: getBuiltInFurnitureMaterial,
  };
}
